import React, { useRef, useEffect } from "react";
import tippy, {
  PopperElement,
  Props,
  roundArrow,
  animateFill as animateFillPlugin,
  Instance as TippyInstance,
} from "tippy.js";
import clsx from "clsx";

type TippyProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
  C,
  {
    getRef?: (el: PopperElement | null) => void;
    content: string;
    as?: C;
    options?: Partial<Props>;
  }
>;

const init = <C extends React.ElementType>(
  el: PopperElement,
  props: TippyProps<C>
): TippyInstance => {
  return tippy(el, {
    plugins: [animateFillPlugin],
    content: props.content,
    arrow: roundArrow,
    popperOptions: {
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            rootBoundary: "viewport",
          },
        },
      ],
    },
    animateFill: false,
    animation: "shift-away",
    ...props.options,
  });
};

const Tippy = <C extends React.ElementType = "span">(props: TippyProps<C>) => {
  const tippyRef = useRef<PopperElement>(null);
  const tippyInstanceRef = useRef<TippyInstance | null>(null);
  const Component = props.as || "span";

  useEffect(() => {
    if (props.getRef) {
      props.getRef(tippyRef.current);
    }

    if (tippyRef.current !== null) {
      // Destroy the previous instance if it exists
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.destroy();
      }
      // Create a new tippy instance
      tippyInstanceRef.current = init<C>(tippyRef.current, props);
    }

    // Cleanup function to destroy tippy instance on unmount
    return () => {
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.destroy();
      }
    };
  }, [props.content]);

  const { content, as, options, getRef, className, ...computedProps } = props;
  return (
    <Component
      ref={tippyRef}
      className={clsx(["cursor-pointer", className])}
      {...computedProps}
    >
      {props.children}
    </Component>
  );
};

export default Tippy;
